import { useGifSearch } from "@portal/providers/GifProvider";
import { FC, useEffect, useRef, useState } from "react";
import KLIPYSearchAttribution from "../../../assets/logos/KLIPY-search-attribution-logo.svg";
import { Input } from "../../form/inputs/Input";
import { Spinner } from "../../primitives";

export const GifSearch: FC<{ onGifClick: (gifId: string, searchTerm: string) => void }> = ({
	onGifClick,
}) => {
	const [input, setInput] = useState("");
	const [debouncedInput, setDebouncedInput] = useState("");
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, isError } =
		useGifSearch(debouncedInput);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedInput(input), 400);
		return () => clearTimeout(timer);
	}, [input]);

	// Focus input on mount
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	// Handle scroll to bottom detection
	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

			if (scrollTop + clientHeight >= scrollHeight - 10) {
				if (hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			}
		};

		scrollContainer.addEventListener("scroll", handleScroll);
		return () => scrollContainer.removeEventListener("scroll", handleScroll);
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	return (
		<div className="w-[100vw] h-[50vh] md:w-[40vw] md:max-w-[700px] md:h-[50vh] md:max-h-[500px] flex justify-start flex-col bg-surface-base rounded-md p-4 space-y-4 overflow-y-auto scrollbar-med">
			<div className="w-full h-fit relative">
				<Input
					ref={inputRef}
					placeholder="Search KLIPY"
					onChange={(e) => setInput(e.target.value)}
					value={input}
				/>
				<img
					src={KLIPYSearchAttribution}
					className="absolute top-[12.5px] right-2 max-h-[20px] opacity-60"
				/>
			</div>
			<div>
				{isLoading ? (
					<div className="flex justify-center py-4">
						<Spinner />
					</div>
				) : (
					<div
						ref={scrollContainerRef}
						className="grid grid-cols-3 gap-2 p-4 max-h-[40vh] md:max-h-[40vh] overflow-y-auto"
					>
						{data?.pages.map((page) => {
							return page.gifs.map((gif) => {
								return (
									<img
										className="w-full h-auto object-cover rounded cursor-pointer hover:opacity-80 transition"
										key={gif.id}
										id={gif.id}
										src={gif.preview}
										alt={gif.title}
										onClick={() => {
											onGifClick(gif.id, input);
										}}
									/>
								);
							});
						})}
						{isFetchingNextPage && (
							<div className="col-span-3 flex justify-center py-4">
								<Spinner />
							</div>
						)}
					</div>
				)}
				{isError && <div className="text-red-500">Failed to load GIFs. Please try again.</div>}
			</div>
		</div>
	);
};
